import React, { useEffect, useRef } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { HiOutlineCurrencyDollar } from 'react-icons/hi';

import cx from 'classnames';
import {
  CUSTOM_AMOUNT_MESSAGE,
  DonateNowFormSteps,
  DonateNowFormStep,
  IDonateNowForm,
} from 'utils/checkInterface';
import { scrollToElement } from 'utils/scrollTo';

import Card from 'components/Card/Card';
import HorizontalLeftImageContent from 'components/HorizontalLeftImageContent/HorizontalLeftImageContent';
import SelectItemInput from 'components/SelectItemInput/SelectItemInput';
import SLSFSwitch from 'components/SLSFSwitch/SLSFSwitch';
import { IDonationOption } from 'models/donationOption';
import { Frequency } from 'models/frequency';

import DonateNowSummary from './DonateNowSteps/DonateNowSummary';

import s from './DonateNowForm.module.scss';

type DonateNowFormProps = {
  onPurchase(
    value: IDonateNowForm,
    asAGuest: boolean,
    createNewAccountTabActive: boolean,
  ): void;
  className?: string;
  value: IDonateNowForm;
  onChange(wholeValue: any, key?: string, value?: any): void;
  onStepChange(step: DonateNowFormStep): void;
  donationOptions: IDonationOption[];
  loading?: boolean;
  cardProps?: any;
  step: DonateNowFormStep;
};

const DonateNowForm: React.FC<DonateNowFormProps> = ({
  value: { donationOption, frequency, customAmount, donationDay },
  loading,
  onChange,
  onPurchase,
  className,
  donationOptions,
  cardProps,
  step,
  onStepChange,
}) => {
  const containerRef = useRef<any>(null);
  useEffect(() => {
    if (containerRef?.current && step !== DonateNowFormSteps.selectDonation) {
      scrollToElement(containerRef?.current, 'top');
    }
  }, [step]);
  const stringToInt = (value: string) => parseInt(value, 10);

  const handleChange = (key: string, value: any) => {
    const wholeValue: IDonateNowForm = {
      donationOption: key === 'customAmount' ? undefined : donationOption,
      frequency,
      donationDay,
      customAmount: key === 'donationOption' ? undefined : customAmount,
      [key]: value,
    };
    switch (key) {
      case 'donationOption':
        scrollToElement(containerRef?.current, 'bottom');
        wholeValue.customAmount = '';
        break;
      case 'frequency':
        if (value === Frequency.OnceOff) {
          wholeValue.donationDay = undefined;
        } else {
          wholeValue.donationDay = 28;
        }
        break;
      default:
    }
    onChange(wholeValue, key, value);
  };

  const renderCurrentStep = () => {
    switch (step) {
      case DonateNowFormSteps.selectDonation: {
        return (
          <div>
            <h2
              className={cx(
                'text-center',
                'font-weight-bold',
                'font-smaller',
                s['smaller-heading'],
              )}
            >
              Donate Now
            </h2>
            <Form.Group className="mb-0">
              <SelectItemInput
                noWrap
                value={donationOption}
                onChange={value => handleChange('donationOption', value)}
                options={donationOptions}
                wrapperProps={{
                  className: 'mb-2',
                }}
              >
                {(props: IDonationOption, selected) => {
                  return (
                    <div role="button" tabIndex={0} onKeyPress={() => {}}>
                      <HorizontalLeftImageContent
                        imageUrl={props.image?.url}
                        imageAlt={props.image_alt}
                        withPadding
                        squaredImage
                        className={cx(
                          { 'bg-primary': selected },
                          'border',
                          'border-primary',
                          'card-rounded',
                        )}
                      >
                        <h4
                          className={cx(
                            { 'text-white': selected },
                            'text-bold',
                            'primary-dark-color',
                          )}
                        >
                          ${stringToInt(props?.amount?.toString())}
                        </h4>
                        <p
                          data-content="donation_campaign_option_heading"
                          className={cx({ 'text-white': selected })}
                        >
                          {props.heading}
                        </p>
                      </HorizontalLeftImageContent>
                    </div>
                  );
                }}
              </SelectItemInput>
            </Form.Group>
            <Form.Group>
              <Row className="align-items-center mt-4 px-0">
                <Col lg={6} className="pr-md-0">
                  <Form.Label
                    className={cx(
                      'text-center',
                      'text-md-left',
                      'd-block',
                      s['custom-donation-label'],
                      'text-bold',
                      'm-md-0',
                      'mb-3',
                    )}
                  >
                    Or choose a custom donation
                  </Form.Label>
                </Col>
                <Col lg={6}>
                  <div className="d-flex align-items-center">
                    <HiOutlineCurrencyDollar
                      size={32}
                      className="text-primary mr-1"
                    />
                    <Form.Control
                      required
                      type="number"
                      placeholder="enter amount"
                      value={customAmount}
                      min={1}
                      data-cy="input-donate-now"
                      className="square-amount-input px-1"
                      onChange={({ target: { value } }) => {
                        handleChange(
                          'customAmount',
                          Math.abs(stringToInt(value)),
                        );
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </Form.Group>
            <Row>
              <Col className="text-center py-3 ">
                <Button
                  data-cy="button-next"
                  disabled={!(donationOption || customAmount)}
                  type="button"
                  variant="outline-primary"
                  onClick={() =>
                    onStepChange(DonateNowFormSteps.selectFrequency)
                  }
                >
                  Next
                </Button>
              </Col>
            </Row>
          </div>
        );
      }
      case DonateNowFormSteps.selectFrequency: {
        return (
          <div>
            <Form.Group className="mb-0 mb-3">
              <Card className="bg-primary text-white">
                <Card.Body>
                  <div className="mb-2">
                    <div className="d-flex justify-content-between mb-1">
                      <h4 className={cx('text-bold', 'm-0')}>
                        ${donationOption?.amount || customAmount}
                      </h4>
                      <Button
                        variant="link"
                        className=" text-white text-left p-0 text-button-link"
                        onClick={() =>
                          onStepChange(DonateNowFormSteps.selectDonation)
                        }
                      >
                        Change donation
                      </Button>
                    </div>
                    {donationOption ? (
                      <p>{donationOption.title}</p>
                    ) : (
                      CUSTOM_AMOUNT_MESSAGE
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Form.Group>
            <Card>
              <Card.Body>
                <Form.Group className="m-0 pb-1">
                  <h4 className="text-center mb-2">
                    Thank you for your support
                  </h4>
                  <p className="mb-4 text-grey text-center">
                    Could you donate monthly and become a Guardian of the Surf?
                  </p>
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <h5 className={cx('text-right', 'pr-3', s['donate-label'])}>
                      Once-off Donation
                    </h5>
                    <SLSFSwitch
                      checked={frequency === Frequency.Monthly}
                      onChange={value =>
                        handleChange(
                          'frequency',
                          value ? Frequency.Monthly : Frequency.OnceOff,
                        )
                      }
                    />
                    <h5 className={cx('text-left', 'pl-3', s['donate-label'])}>
                      Monthly Donation
                    </h5>
                  </div>
                  <div className="px-3">
                    <p className="mb-2 mt-md-0 text-center">
                      Sarah 32, gives $52 monthly
                    </p>
                    <p className="text-center font-italic">
                      "Becoming a Guardian of the Surf was a real no-brainer for
                      me. Its the least I can do to help keep surf lifesavers on
                      our beaches and costs me less than a cup of coffee each
                      day"
                    </p>
                  </div>
                </Form.Group>
              </Card.Body>
            </Card>
            <div className="text-center py-3">
              <Button
                data-cy="button-next-thank-you"
                type="button"
                variant="outline-primary"
                className="mt-3"
                onClick={() =>
                  onStepChange(
                    frequency === Frequency.OnceOff
                      ? DonateNowFormSteps.summary
                      : DonateNowFormSteps.selectDonationDate,
                  )
                }
              >
                Next
              </Button>
            </div>
          </div>
        );
      }
      case DonateNowFormSteps.selectDonationDate: {
        return (
          <div>
            <Form.Group className="mb-0 mb-3">
              <Card className="bg-primary text-white">
                <Card.Body>
                  <div className="mb-2">
                    <div className="d-flex justify-content-between mb-1">
                      <h4 className={cx('text-bold', 'm-0')}>
                        $
                        {stringToInt(donationOption?.amount?.toString()) ||
                          stringToInt(customAmount?.toString() || '0')}
                      </h4>
                      <Button
                        variant="link"
                        className=" text-white text-left p-0 text-button-link"
                        onClick={() =>
                          onStepChange(DonateNowFormSteps.selectDonation)
                        }
                      >
                        Change donation
                      </Button>
                    </div>
                    {donationOption ? (
                      <p>{donationOption.title}</p>
                    ) : (
                      CUSTOM_AMOUNT_MESSAGE
                    )}
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <div>
                      <h5>Frequency</h5>
                      <h4 className={cx('text-bold', 'm-0')}>
                        {frequency === Frequency.OnceOff
                          ? 'Single'
                          : Frequency.Monthly}
                      </h4>
                    </div>
                    <Button
                      variant="link"
                      className=" text-white text-left p-0 text-button-link"
                      onClick={() =>
                        onStepChange(DonateNowFormSteps.selectFrequency)
                      }
                    >
                      Change frequency
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Form.Group>
            <Card>
              <Card.Body>
                <Form.Group className="m-0 pb-1">
                  <h4 className="text-center mb-2">Choose day of donation</h4>
                  <p className="mb-4 text-grey text-center">
                    You can choose to make your donation on either the 15th or
                    28th of the month.
                  </p>
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <h5 className={cx('text-right', 'pr-3', s['donate-label'])}>
                      15th of Month
                    </h5>
                    <SLSFSwitch
                      checked={donationDay === 28}
                      onChange={value =>
                        handleChange('donationDay', value ? 28 : 15)
                      }
                    />
                    <h5 className={cx('text-left', 'pl-3', s['donate-label'])}>
                      28th of Month
                    </h5>
                  </div>
                  <div className="px-3">
                    <p className="mb-2 mt-md-0 text-center">
                      Mark 45, gives $100 monthly
                    </p>
                    <p className="text-center font-italic">
                      "Volunteer surf lifesavers saved my little boy from a rip
                      so I donate monthly, on a day that works best for me, as I
                      cannot thank them enough for what they have done"
                    </p>
                  </div>
                </Form.Group>
              </Card.Body>
            </Card>
            <div className="text-center mt-3">
              <Button
                type="button"
                variant="outline-primary"
                className="my-3"
                onClick={() => onStepChange(DonateNowFormSteps.summary)}
              >
                Next
              </Button>
            </div>
          </div>
        );
      }
      case DonateNowFormSteps.summary: {
        return (
          <DonateNowSummary
            donationOption={donationOption}
            onStepChange={onStepChange}
            onPurchase={onPurchase}
            frequency={frequency}
            customAmount={customAmount}
            donationDay={donationDay}
            canChangeFrequency
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <Card
      className={cx(
        'donate-now-card-mobile',
        'position-relative',
        'overflow-hidden',
        className,
      )}
      loading={loading}
      {...cardProps}
    >
      <Card.Body ref={containerRef}>
        <Form onSubmit={ev => ev.preventDefault()}>{renderCurrentStep()}</Form>
      </Card.Body>
    </Card>
  );
};

export default DonateNowForm;
